import { MapPin, Drop, QrCode } from '@phosphor-icons/react';
import type { PiezaFormaleta } from '@/api/formaleta';
import { Fecha, Foto, Pastilla, SinRegistrar } from './comunes';

// La tarjeta de UNA pieza física. Es el modo que Daniel pidió al lado del
// agrupado: «al fin y al cabo, las personas van a buscar las piezas
// puntualmente, no las agrupaciones […] buscar una pieza que necesite y ver si
// hay alguna disponible o, si no hay disponible, cuál es la más cercana o dónde
// están».
//
// Lo primero que se lee es el CONSECUTIVO, en mono y grande: es lo que está
// marcado en el aluminio y lo único que une la pieza en la mano con la fila de
// la base.

export function TarjetaPieza({ pieza, compacta }: {
  pieza: PiezaFormaleta;
  /** Dentro del despliegue de un modelo no se repite el nombre del modelo. */
  compacta?: boolean;
}) {
  return (
    <article className="flex items-stretch gap-0 overflow-hidden rounded-lg border border-brand-gray-200 bg-white">
      {/* La barra de color de la categoría: identidad visual a distancia, antes
          de leer una sola letra. */}
      <span
        style={{ backgroundColor: pieza.colorHex || '#D9D9D9' }}
        className="w-1.5 shrink-0"
      />

      <div className="flex min-w-0 flex-1 items-center gap-3 p-2.5">
        {!compacta && <Foto url={pieza.urlFoto} alt={pieza.modelo || 'pieza'} lado={52} />}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-mono text-base font-bold leading-none text-brand-gray-500">
              {pieza.consecutivo || <SinRegistrar que="sin consecutivo" />}
            </p>
            {pieza.abreviatura
              ? <Pastilla texto={pieza.abreviatura} colorHex={pieza.colorHex} titulo={pieza.categoria || undefined} />
              : <SinRegistrar que="sin categoría" detalle="El renglón no tiene IDFormCategoria: son los accesorios, el 93% de las piezas." />}
            {pieza.estado
              ? <Pastilla texto={pieza.estado} colorHex={pieza.colorEstado} />
              : <SinRegistrar que="sin estado" detalle="IDFormEstadoPieza en NULL." />}
          </div>

          {!compacta && (
            // El nombre crudo del fabricante va SECUNDARIO y en mono, nunca
            // como identificador principal: es jerga técnica en inglés que la
            // cuadrilla no usa.
            <p className="mt-1 truncate font-mono text-[11px] text-brand-gray-400"
              title={pieza.modelo || undefined}>
              {pieza.modelo || '—'}
            </p>
          )}

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-1 text-[11px] text-brand-gray-400">
              <MapPin size={12} weight="bold" />
              {pieza.idObra || pieza.ubicacion
                ? <span className="font-medium text-brand-gray-500">
                    {[pieza.idObra, pieza.ubicacion].filter(Boolean).join(' · ')}
                  </span>
                // «¿dónde está?» es una de las dos preguntas de campo, y hoy
                // FormMovimiento tiene 0 filas: el paradero empieza a existir
                // cuando se registre el primer movimiento.
                : <SinRegistrar que="sin paradero" detalle="No se ha registrado ningún movimiento de esta pieza." />}
            </span>

            <span className="flex items-center gap-1 text-[11px] text-brand-gray-400">
              <Drop size={12} weight="bold" />
              <Fecha valor={pieza.fechaUltLavado} que="sin lavado" />
            </span>

            <span className="flex items-center gap-1 text-[11px] text-brand-gray-400">
              <QrCode size={12} weight="bold" />
              {pieza.fechaMarcado
                ? <Fecha valor={pieza.fechaMarcado} que="sin marcar" />
                // NULL = está en la base pero la pieza en la mano sigue siendo
                // anónima. Es la diferencia entre inventariar y marcar.
                : <SinRegistrar que="sin marcar" detalle="La pieza existe en la base pero no tiene marca física." />}
            </span>
          </div>
        </div>

        {pieza.latitud !== null && pieza.longitud !== null && (
          <a
            href={`https://www.google.com/maps?q=${pieza.latitud},${pieza.longitud}`}
            target="_blank" rel="noreferrer"
            title="Abrir la coordenada del último movimiento"
            className="shrink-0 rounded border border-brand-gray-200 p-2 text-brand-gray-400 hover:border-brand-gray-300 hover:text-brand-gray-500"
          >
            <MapPin size={16} weight="bold" />
          </a>
        )}
      </div>
    </article>
  );
}
